import { useEffect, useMemo, useState, useCallback } from "react";
import api, { inr, formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { Search, X, Filter, FileText, ChevronRight } from "lucide-react";
import { SavedFiguresTag, SAVED_PAIR_NOTE } from "@/components/SavedFiguresTag";

const fmtPeriod = (s) => String(s || "").replace(/_/g, " ").replace(/\bto\b/, "→");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtTime = (s) => (s ? new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " IST" : "—");
const fmtDateTime = (s) => (s ? `${fmtDate(s)} · ${fmtTime(s)}` : "—");
const spF = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const lrF = (a) => (Array.isArray(a) ? `${spF(a[0])} / ${spF(a[1])}` : "—");
const TRAIL_COLS = [["opening", "Opening"], ["new", "Added"], ["available", "Before matching"], ["consumed", "Consumed"], ["remaining", "Closing"]];

/** R-26: pair and bonus SP for one week — Opening + Added − Consumed = Closing (Left / Right). */
function TrailTable({ pair, bonus, pairSaved }) {
  return (
    <section>
      <h4 className="text-[10px] uppercase tracking-widest text-navy/70 font-bold mb-2">SP Balance · This Week (Left / Right)</h4>
      <div className="overflow-x-auto border border-border rounded-sm">
        <table className="w-full text-xs" data-testid="wph-trail">
          <thead className="bg-navy/5">
            <tr>
              <th className="text-left px-3 py-2 uppercase tracking-wider font-medium text-navy/80">Balance</th>
              {TRAIL_COLS.map(([k, h]) => <th key={k} className="text-right px-3 py-2 uppercase tracking-wider font-medium text-navy/80 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {[["Pair", pair], ["Bonus", bonus]].map(([name, b]) => (
              <tr key={name} className="border-t border-border">
                <td className="px-3 py-2 font-medium text-navy">
                  {name}
                  {name === "Pair" && pairSaved ? <div><SavedFiguresTag testid="wph-trail-pair-saved" /></div> : null}
                </td>
                {TRAIL_COLS.map(([k]) => (
                  <td key={k} data-testid={`wph-trail-${name.toLowerCase()}-${k}`}
                    className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${k === "remaining" ? "font-bold text-emerald-800" : "text-navy"}`}>{lrF(b?.[k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-navy/70 mt-1">Opening + Added − Consumed = Closing. Pair SP and bonus SP are kept separately.</p>
      {pairSaved ? <p className="text-[10px] text-sky-900 mt-1" data-testid="wph-trail-saved-note">{SAVED_PAIR_NOTE}</p> : null}
    </section>
  );
}

/** Table rows for a week with no closing row (reconstructed) or the week now running. */
function trailWeekRows(w, current) {
  const id = current ? "current" : w.period_label;
  const p = w.pair, b = w.bonus;
  const label = current ? "No finalized closing" : "No closing recorded";
  return [
    <tr key={`h-${id}`} data-testid={`wph-trail-group-${id}`} className={current ? "bg-emerald-50/70" : "bg-amber-50/60"}>
      <td colSpan={16} className="px-3 py-2 text-navy border-t border-b-2 border-navy/20">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="text-[10px] uppercase tracking-widest text-navy/70 font-bold">{current ? "This week · live" : "Weekly Closing"}</span>
          <span className="font-serif text-base text-navy">{fmtPeriod(w.period_label)}</span>
          <span className="text-xs font-bold text-amber-800" data-testid={`wph-trail-label-${id}`}>{label}</span>
        </div>
      </td>
    </tr>,
    <tr key={`r-${id}`} data-testid={`wph-trail-row-${id}`} className="border-b border-border bg-white">
      <td className="px-3 py-2.5 text-navy/60 text-xs">*</td>
      <td colSpan={2} className="px-3 py-2.5 text-xs text-navy/80 italic whitespace-nowrap">{label}</td>
      <td className="px-3 py-2.5 tabular-nums text-navy/70">{spF(p.available[0])} → <span className="text-navy font-semibold">{spF(p.remaining[0])}</span></td>
      <td className="px-3 py-2.5 tabular-nums text-navy/70">{spF(p.available[1])} → <span className="text-navy font-semibold">{spF(p.remaining[1])}</span></td>
      <td className="px-3 py-2.5 tabular-nums text-navy">{current ? Math.floor(Number(p.consumed[0] || 0) / 50) : "—"}</td>
      <td colSpan={6} className="px-3 py-2.5 text-xs text-navy/80">
        {current ? `Pair ${inr(w.pair_income_rs)} so far · settles at the next weekly closing` : "No closing recorded · nothing paid this week"}
      </td>
      <td className="px-3 py-2.5 tabular-nums text-emerald-700" data-testid={`wph-trail-pair-carry-${id}`}>
        {lrF(p.remaining)}*
        {w.pair_source === "saved" ? <div><SavedFiguresTag kind={current ? "opening" : "carried"} testid={`wph-trail-pair-saved-${id}`} /></div> : null}
      </td>
      <td className="px-3 py-2.5 tabular-nums text-emerald-700" data-testid={`wph-trail-bonus-carry-${id}`}>{lrF(b.remaining)}*</td>
      <td className="px-3 py-2.5">
        <span className="inline-flex text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-navy/80 whitespace-nowrap">{current ? "Running" : "No closing recorded"}</span>
      </td>
      <td />
    </tr>,
  ];
}

function StatusPill({ status, gross_rs }) {
  // The ledger only ever carries COMMITTED closings — so the "closing status" is
  // implicitly ✓ Closed for every row here (see the Closed On column + closed_at).
  // What this pill reports is the PAYMENT status, separately:
  //   • status === "paid" + gross > 0           → ✓ Paid
  //   • status === "paid" + gross === 0         → — Zero Payout (nothing to pay)
  //   • status === "pending_bank_transfer"/else → ⏱ Payment Pending (closing done, bank transfer awaiting)
  const zero = Number(gross_rs || 0) === 0;
  if (status === "paid" && !zero) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800" data-testid="wph-status">
        ✓ Paid
      </span>
    );
  }
  if (status === "paid" && zero) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600" data-testid="wph-status">
        — Zero Payout
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 whitespace-nowrap" data-testid="wph-status">
      ⏱ Closing finalized · Payment pending
    </span>
  );
}

function DetailModal({ row, onClose }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    if (!row) return;
    setBusy(true);
    api
      .get(`/dist/weekly-payouts/${row.id}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load detail"))
      .finally(() => setBusy(false));
  }, [row]);
  if (!row) return null;
  const d = detail || row;
  const tier = d.monthly_bonus_tier || {};
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="wph-detail-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white rounded-lg border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gradient-to-r from-navy to-navy/90 text-white px-5 py-3.5 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gold">Weekly Closing Detail</div>
            <div className="font-serif text-lg" data-testid="wph-detail-period">{fmtPeriod(d.period_label)}</div>
            <div className="text-[11px] text-white/60">Closed: {fmtDate(d.closed_at)}</div>
          </div>
          <button
            data-testid="wph-detail-close"
            onClick={onClose}
            className="text-white/70 hover:text-white p-1"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Header stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-navy/5 border border-navy/10 rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-navy/50">Gross</div>
              <div className="text-2xl text-navy tabular-nums" data-testid="wph-detail-gross">{inr(d.gross_rs)}</div>
              {Number(d.withdrawn_deduction_rs || 0) > 0 ? (
                <div className="text-[11px] text-rose-700 mt-1" data-testid="wph-detail-withdrawn">Less: already withdrawn − {inr(d.withdrawn_deduction_rs)}</div>
              ) : null}
            </div>
            <div className="bg-rose/5 border border-rose/10 rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-rose/70">TDS ({Number(d.tds_rate_pct || 0).toFixed(0)}%)</div>
              <div className="text-2xl text-rose tabular-nums">− {inr(d.tds_amount_rs)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-emerald-700">Net</div>
              <div className="text-2xl text-emerald-800 tabular-nums" data-testid="wph-detail-net">{inr(d.net_rs)}</div>
            </div>
            <div className="bg-white border border-border rounded-sm p-3 flex flex-col justify-between">
              <div className="text-[9px] uppercase tracking-widest text-navy/50">Payment Status</div>
              <StatusPill status={d.status} gross_rs={d.gross_rs} />
              {d.paid_at ? <div className="text-[10px] text-navy/50 mt-1">Paid on {fmtDate(d.paid_at)}</div> : null}
            </div>
          </div>

          {/* Commission breakdown */}
          <section>
            <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">Commission Breakdown</h4>
            <table className="w-full text-sm">
              <tbody>
                {d.pair_income_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">Pair Matching (1:1) — {d.matched_sp} SP · {d.matched_pairs} pairs</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.pair_income_rs)}</td>
                  </tr>
                )}
                {d.direct_ref_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">Direct Referral 10%</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.direct_ref_rs)}</td>
                  </tr>
                )}
                {d.monthly_bonus_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">
                      Monthly Bonus{tier.tier ? ` — Tier ${tier.tier} (${tier.high_sp}:${tier.match_sp} SP)` : ""}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.monthly_bonus_rs)}</td>
                  </tr>
                )}
                {d.rank_reward_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">Rank Reward</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.rank_reward_rs)}</td>
                  </tr>
                )}
                <tr className="bg-navy">
                  <td className="px-3 py-2 text-gold font-extrabold uppercase tracking-wide text-xs">Gross</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gold font-extrabold">{inr(d.gross_rs)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Leg SP evolution */}
          {d.pair_trail ? <TrailTable pair={d.pair_trail} bonus={d.bonus_trail} pairSaved={(d.pair_source || row.pair_source) === "saved"} /> : (
          <section>
            <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">Leg SP · This Closing</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy/50">Left SP</div>
                <div className="text-sm tabular-nums text-navy mt-1">
                  <span className="text-navy/60">Before</span> {d.left_sp_before} <span className="text-navy/40 mx-1">→</span>{" "}
                  <span className="font-bold text-emerald-700">After</span> <span className="text-emerald-700 font-extrabold">{d.left_sp_after}</span>
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy/50">Right SP</div>
                <div className="text-sm tabular-nums text-navy mt-1">
                  <span className="text-navy/60">Before</span> {d.right_sp_before} <span className="text-navy/40 mx-1">→</span>{" "}
                  <span className="font-bold text-emerald-700">After</span> <span className="text-emerald-700 font-extrabold">{d.right_sp_after}</span>
                </div>
              </div>
            </div>
          </section>
          )}

          {/* Carry-forward */}
          <section>
            <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">Carry-forward into Next Closing</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50/70 border border-emerald-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700">Pair Carry L / R</div>
                <div className="text-xl text-emerald-800 tabular-nums" data-testid="wph-detail-pair-carry">
                  {d.pair_carry_left_sp} <span className="text-emerald-800/40 mx-1">/</span> {d.pair_carry_right_sp}
                  <span className="text-emerald-700/60 ml-2 text-xs">SP</span>
                </div>
              </div>
              <div className="bg-emerald-50/70 border border-emerald-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700">Bonus Carry L / R</div>
                <div className="text-xl text-emerald-800 tabular-nums" data-testid="wph-detail-bonus-carry">
                  {d.bonus_carry_left_sp} <span className="text-emerald-800/40 mx-1">/</span> {d.bonus_carry_right_sp}
                  <span className="text-emerald-700/60 ml-2 text-xs">SP</span>
                </div>
              </div>
            </div>
          </section>

          {/* Underlying ledger entries */}
          {busy ? (
            <div className="text-xs text-navy/50 italic">Loading ledger entries…</div>
          ) : (d.incomes || []).length > 0 ? (
            <section>
              <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">
                Underlying Income Entries ({(d.incomes || []).length})
              </h4>
              <div className="border border-border rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-navy/5">
                    <tr>
                      {["Date", "Type", "Matched SP", "Amount", "Paid Out"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 uppercase tracking-wider font-medium text-navy/60">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(d.incomes || []).map((i) => (
                      <tr key={i.id} className="border-t border-border">
                        <td className="px-3 py-1.5 text-navy/70 tabular-nums">{fmtDate(i.created_at)}</td>
                        <td className="px-3 py-1.5 text-navy capitalize">{String(i.type || "").replace(/_/g, " ")}</td>
                        <td className="px-3 py-1.5 text-navy/70 tabular-nums">{i.matched_sp ?? "—"}</td>
                        <td className="px-3 py-1.5 text-navy font-semibold tabular-nums">{inr(i.amount)}</td>
                        <td className="px-3 py-1.5">
                          {i.paid_out ? (
                            <span className="text-emerald-700 text-[10px] font-bold">✓ paid</span>
                          ) : (
                            <span className="text-amber-700 text-[10px] font-bold">— pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <div className="text-[10px] text-navy/50 italic border-t border-border pt-3">
            This record is append-only. Once committed, weekly closings are permanent and cannot be altered.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DistWeeklyPayoutHistory() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_gross_rs: 0, total_tds_rs: 0, total_net_rs: 0 });
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [period, setPeriod] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [noClosing, setNoClosing] = useState([]);
  const [current, setCurrent] = useState(null);

  const periodOptions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.period_label && s.add(r.period_label));
    return Array.from(s);
  }, [rows]);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (fromDate) qs.set("from_date", fromDate);
    if (toDate) qs.set("to_date", toDate);
    if (period) qs.set("period_label", period);
    api
      .get(`/dist/weekly-payouts${qs.toString() ? `?${qs.toString()}` : ""}`)
      .then((r) => {
        setRows(r.data.rows || []);
        setNoClosing(r.data.no_closing_weeks || []);
        setCurrent(r.data.current_week || null);
        setTotals({
          total_gross_rs: r.data.total_gross_rs || 0,
          total_tds_rs:   r.data.total_tds_rs   || 0,
          total_net_rs:   r.data.total_net_rs   || 0,
        });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load history"))
      .finally(() => setLoading(false));
  }, [fromDate, toDate, period]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setFromDate(""); setToDate(""); setPeriod(""); };

  return (
    <DashboardLayout
      title="Weekly Payout History"
      subtitle="Every committed weekly closing · read-only · permanent"
    >
      {/* Totals strip — no zero tiles when nothing is recorded */}
      {!loading && rows.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-5 mb-5" data-testid="wph-no-closing">
          <div className="font-serif text-lg text-navy">No closing recorded</div>
          <div className="text-xs text-navy/80 mt-1">
            {fromDate || toDate || period ? "No closing matches the selected filters." : "Your weekly closings will appear here once the first one is finalized."}
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-border rounded-sm p-4" data-testid="wph-total-count">
          <div className="text-[10px] uppercase tracking-widest text-navy/50">Closings</div>
          <div className="text-2xl text-navy tabular-nums">{rows.length}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4" data-testid="wph-total-gross">
          <div className="text-[10px] uppercase tracking-widest text-navy/50">Gross</div>
          <div className="text-2xl text-navy tabular-nums">{inr(totals.total_gross_rs)}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-rose/70">TDS</div>
          <div className="text-2xl text-rose tabular-nums">− {inr(totals.total_tds_rs)}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4" data-testid="wph-total-net">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Net</div>
          <div className="text-2xl text-emerald-800 tabular-nums">{inr(totals.total_net_rs)}</div>
        </div>
      </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-border rounded-sm p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-navy/50" />
          <span className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">From date</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="wph-filter-from"
              className="w-full mt-1 border border-border rounded-sm px-3 py-2 text-sm focus:border-gold outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">To date</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="wph-filter-to"
              className="w-full mt-1 border border-border rounded-sm px-3 py-2 text-sm focus:border-gold outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">Closing period</span>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="wph-filter-period"
              className="w-full mt-1 border border-border rounded-sm px-3 py-2 text-sm focus:border-gold outline-none bg-white"
            >
              <option value="">All periods</option>
              {periodOptions.map((p) => (
                <option key={p} value={p}>{fmtPeriod(p)}</option>
              ))}
            </select>
          </label>
          <div className="flex items-end gap-2">
            <button
              onClick={clearFilters}
              data-testid="wph-filter-clear"
              className="w-full px-3 py-2 text-xs uppercase tracking-widest text-navy/60 border border-border hover:bg-slate-50 rounded-sm"
            >
              <X size={12} className="inline mr-1" /> Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-navy text-white">
            <tr>
              {[
                "#", "Closed On", "Paid On", "Left → After", "Right → After", "Matched Pairs",
                "Pair ₹", "Dref ₹", "Bonus ₹", "Gross ₹", "TDS", "Net ₹",
                "Pair Carry L/R", "Bonus Carry L/R", "Payment Status", ""
              ].map((h) => (
                <th key={h} className="text-left px-3 py-3 text-[10px] uppercase tracking-widest font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={16} className="p-6 text-center text-navy/50">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={16} className="p-6 text-center text-navy/50" data-testid="wph-empty">No closing recorded</td></tr>
            )}
            {!loading && (() => {
              const groups = [];
              rows.forEach((r) => {
                const g = groups[groups.length - 1];
                if (g && g.period === r.period_label) g.rows.push(r);
                else groups.push({ period: r.period_label, rows: [r] });
              });
              const bands = ["bg-white", "bg-slate-50/40"];
              if (rows.length === 0) return null;
              const items = [
                ...groups.map((g) => ({ end: g.rows[0]?.period_end_date || "", g })),
                ...noClosing.map((w) => ({ end: w.period_end_date || "", w })),
              ].sort((a, b) => (a.end < b.end ? 1 : a.end > b.end ? -1 : 0));
              return [...(current ? trailWeekRows(current, true) : []), ...items.flatMap((it, gi) => {
                if (it.w) return trailWeekRows(it.w, false);
                const g = it.g;
                const grossSum = g.rows.reduce((s, r) => s + (r.gross_rs || 0), 0);
                const netSum   = g.rows.reduce((s, r) => s + (r.net_rs   || 0), 0);
                const closed_on = g.rows[0]?.closed_at;
                const paid_on   = g.rows[0]?.paid_at;
                return [
                  <tr key={`h-${g.period}`} data-testid={`wph-group-${g.period}`} className="bg-navy/5">
                    <td colSpan={16} className="px-3 py-2 text-navy border-t border-b-2 border-navy/20">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                        <span className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Weekly Closing</span>
                        <span className="font-serif text-base text-navy">{fmtPeriod(g.period)}</span>
                        <span className="text-xs text-navy/60">· Closed {fmtDateTime(closed_on)}</span>
                        {paid_on ? <span className="text-xs text-emerald-700">· Paid {fmtDateTime(paid_on)}</span> : null}
                        <span className="ml-auto text-xs text-navy/70">
                          Gross <span className="tabular-nums font-bold text-navy">{inr(grossSum)}</span>
                          <span className="mx-2 text-navy/30">·</span>
                          Net <span className="tabular-nums font-bold text-emerald-700">{inr(netSum)}</span>
                        </span>
                      </div>
                    </td>
                  </tr>,
                  ...g.rows.map((r, ri) => (
                    <tr
                      key={r.id}
                      data-testid={`wph-row-${r.id}`}
                      onClick={() => setOpenRow(r)}
                      className={`${bands[gi % bands.length]} border-b border-border hover:bg-cream/40 cursor-pointer`}
                    >
                      <td className="px-3 py-2.5 text-navy/50 tabular-nums text-xs">{ri + 1}<span className="text-navy/30">/{g.rows.length}</span></td>
                      <td className="px-3 py-2.5 text-navy/70 text-xs whitespace-nowrap">
                        {fmtDate(r.closed_at)}
                        <div className="text-navy/40 text-[10px] tabular-nums">{fmtTime(r.closed_at)}</div>
                      </td>
                      <td className="px-3 py-2.5 text-navy/70 text-xs whitespace-nowrap">
                        {r.paid_at ? fmtDate(r.paid_at) : "—"}
                        {r.paid_at ? <div className="text-emerald-700/70 text-[10px] tabular-nums">{fmtTime(r.paid_at)}</div> : null}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/70">{r.left_sp_before} → <span className="text-navy font-semibold">{r.left_sp_after}</span></td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/70">{r.right_sp_before} → <span className="text-navy font-semibold">{r.right_sp_after}</span></td>
                      <td className="px-3 py-2.5 tabular-nums text-navy">{r.matched_pairs}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">{r.pair_income_rs > 0 ? inr(r.pair_income_rs) : "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">{r.direct_ref_rs > 0 ? inr(r.direct_ref_rs) : "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">{r.monthly_bonus_rs > 0 ? inr(r.monthly_bonus_rs) : "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy font-semibold">{inr(r.gross_rs)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-rose">− {inr(r.tds_amount_rs)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-emerald-700 font-bold">
                        {inr(r.net_rs)}
                        {Number(r.withdrawn_deduction_rs || 0) > 0 ? (
                          <div className="text-[10px] font-medium text-rose-700 whitespace-nowrap" data-testid={`wph-row-withdrawn-${r.id}`}>less already withdrawn − {inr(r.withdrawn_deduction_rs)}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">
                        <span className="text-emerald-700">{r.pair_carry_left_sp}</span>
                        <span className="text-navy/40 mx-1">/</span>
                        <span className="text-emerald-700">{r.pair_carry_right_sp}</span>
                        {r.pair_source === "saved" ? <div><SavedFiguresTag testid={`wph-row-pair-saved-${r.id}`} /></div> : null}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">
                        <span className="text-emerald-700">{r.bonus_carry_left_sp}</span>
                        <span className="text-navy/40 mx-1">/</span>
                        <span className="text-emerald-700">{r.bonus_carry_right_sp}</span>
                      </td>
                      <td className="px-3 py-2.5"><StatusPill status={r.status} gross_rs={r.gross_rs} /></td>
                      <td className="px-3 py-2.5 text-navy/40"><ChevronRight size={14} /></td>
                    </tr>
                  )),
                ];
              })];
            })()}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (current || noClosing.length > 0) ? (
        <p className="mt-3 text-[11px] text-navy/80" data-testid="wph-reconstructed-note">
          * Reconstructed balance — not a saved closing result. Pair and bonus SP are rebuilt week by week from recorded SP additions and consumption.
        </p>
      ) : null}
      {[...rows, ...noClosing, ...(current ? [current] : [])].some((x) => x.pair_source === "saved") ? (
        <p className="mt-2 text-[11px] text-sky-900" data-testid="wph-saved-note">{SAVED_PAIR_NOTE}</p>
      ) : null}
      <p className="mt-4 text-[10px] text-navy/50 italic">
        <FileText size={10} className="inline mr-1" />
        This is a permanent, read-only ledger. Committed weekly closings can never be modified, deleted, or recalculated.
      </p>

      {openRow && <DetailModal row={openRow} onClose={() => setOpenRow(null)} />}
    </DashboardLayout>
  );
}
